import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import api from '@/lib/api';

const POLL_MS = 45000;

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread_count');
      if (mounted.current) setUnread(data.count || 0);
    } catch (err) {
      /* ignore transient errors */
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications');
      if (mounted.current) setItems(data || []);
    } catch (err) {
      /* ignore */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [fetchUnread]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchList();
  };

  const handleItemClick = async (item) => {
    if (!item.is_read) {
      try {
        await api.post(`/notifications/${item.id}/read`);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
        setUnread((u) => Math.max(0, u - 1));
      } catch (err) {
        /* ignore */
      }
    }
    setAnchorEl(null);
    if (item.appointment_id) navigate('/calendar');
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read_all');
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (err) {
      /* ignore */
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen} size="small" data-testid="notification-bell" sx={{ color: 'text.primary' }}>
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, maxHeight: 440 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Notifications
          </Typography>
          {items.some((n) => !n.is_read) && (
            <Button size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflow: 'auto' }}>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleItemClick(item)}
                sx={{ alignItems: 'flex-start', bgcolor: item.is_read ? 'transparent' : 'action.hover' }}
              >
                <ListItemText
                  primary={item.message}
                  secondary={relativeTime(item.created_at)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: item.is_read ? 400 : 700 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
